/**
 * ForecastCard Component
 *
 * UI/Aesthetics: Card component displaying rebate forecast data with interactive line chart and optional status cards
 * Functions:
 * - Displays forecast data using Recharts line chart
 * - Shows actual vs predicted rebate amounts over time
 * - Custom tooltip with formatted currency values
 * - Optional status cards integration
 * - Responsive chart sizing and formatting
 * Previous: Used MUI Card + MDBox + MDTypography with sx styling
 * Now: Uses shadcn Card + native elements with Tailwind classes
 */

import React, { useMemo } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  TooltipProps,
  ReferenceLine,
  LegendProps,
} from 'recharts';
import { MultiYearForecastData } from '@/types/rebateAnalytics';
import colors from 'src/assets/md-theme/base/colors';
import RebateStatusCards from 'src/components/rebates/RebateStatusCards';
import {
  transformForecastData,
  convertToRechartsData,
  chartFormatters,
  chartConfig,
  RechartsDataPoint,
} from 'src/utils/chartUtils';
import { calculateConditionalVsNonConditionalRebates } from 'src/utils/rebateUtils';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import {
  formatCurrency,
  calculateOverallRebateMetrics,
} from '@erp/shared-utils';

// Available prediction algorithm options with colors
export const PREDICTION_ALGORITHMS = {
  'linear-regression': { label: 'Linear Regression', color: '#3B82F6' }, // Blue (default)
  'ai-forecasting': { label: 'AI Forecasting', color: '#F97316' }, // Orange
  sarima: { label: 'SARIMA', color: '#EF4444' }, // Red
  'yoy-seasonal': { label: 'YOY Seasonal Curves', color: '#06B6D4' }, // Cyan
} as const;

export type PredictionAlgorithm = keyof typeof PREDICTION_ALGORITHMS;

// Helper to get all algorithm keys as an array (for default state initialization)
export const ALL_PREDICTION_ALGORITHMS = Object.keys(
  PREDICTION_ALGORITHMS,
) as PredictionAlgorithm[];

interface ForecastCardProps {
  multiYearForecastData?: MultiYearForecastData;
  title?: string;
  showStatusCards?: boolean;
  compact?: boolean;
  rebateRules?: Array<{
    id: string;
    name?: string;
    isConditional: boolean;
  }>;
  isLoadingForecast?: boolean; // Whether forecast data is loading
  isCumulative?: boolean; // Whether data is cumulative (affects status card labels)
  currentDate?: Date; // Optional current date for date-based calculations. Defaults to new Date() if not provided.
  predictionSplitDay?: number | null; // Optional split day for past/future predictions. If provided, will be used instead of calculating it.
  lastRealEstimate?: {
    daysFromStart: number;
    totalRebateAmount: number;
    date: Date;
  } | null; // Optional last real estimate point. If provided, will be used instead of calculating it.
  ruleTagsMap?: Map<string, string[]>; // Map of ruleId -> tag names
  tagNameToIdMap?: Map<string, string>; // Map of tag name -> tag ID
  visibleLines?: Set<string>; // Set of visible line types (includes algorithm names like 'linear-regression', 'ai-forecasting', etc.)
  primaryLine?: string; // Currently selected primary line (not used by chart, passed for consistency)
  onVisibleLinesChange?: (lines: Set<string>) => void; // Handler for visibility changes (optional)
}

export type { ForecastCardProps };

const ForecastCard: React.FC<ForecastCardProps> = ({
  multiYearForecastData,
  title,
  showStatusCards = false,
  compact = false,
  rebateRules,
  isLoadingForecast = false,
  isCumulative = false,
  currentDate,
  predictionSplitDay,
  lastRealEstimate,
  ruleTagsMap,
  tagNameToIdMap,
  visibleLines,
  primaryLine,
  onVisibleLinesChange,
}) => {
  // Default to all lines visible if not provided
  const effectiveVisibleLines = useMemo(() => {
    if (!visibleLines) {
      return new Set([
        'estimates',
        ...ALL_PREDICTION_ALGORITHMS,
        'reported',
        'previousYear',
      ]);
    }
    return visibleLines;
  }, [visibleLines]);

  // Predictions are gated behind estimates - they can only be visible when estimates is ON
  const isEstimatesVisible = effectiveVisibleLines.has('estimates');

  // Helper to check if a prediction algorithm should be rendered
  // Prediction visibility requires both: algorithm is in visibleLines AND estimates is visible
  const isPredictionEffectivelyVisible = (algorithm: string): boolean => {
    return effectiveVisibleLines.has(algorithm) && isEstimatesVisible;
  };

  // Use multiYearForecastData if provided, otherwise fall back to legacy forecast prop
  // This is the transformed data (cumulative if isCumulative=true, incremental if false)
  const rawCurrentYearData = multiYearForecastData?.currentYearData;
  const previousYearData = multiYearForecastData?.previousYearData ?? null;

  // Don't filter predictions - pass all data through so chart can render all lines
  // Visibility is controlled at the Line component level
  const currentYearData = rawCurrentYearData;

  // Data comes from backend with both incremental and cumulative values
  // We just select which field to display based on isCumulative flag
  const processedCurrentYearData = useMemo(() => {
    if (!currentYearData) return null;
    return transformForecastData(
      currentYearData,
      isCumulative,
      predictionSplitDay,
    );
  }, [currentYearData, isCumulative, predictionSplitDay]);

  // Transform previous year data if available
  // Note: previous year doesn't use predictionSplitDay/lastRealEstimate (those are for current year)
  const processedPreviousYearData = useMemo(() => {
    if (!previousYearData) {
      return null;
    }
    const processed = transformForecastData(previousYearData, isCumulative);
    return processed;
  }, [previousYearData, isCumulative]);

  // Convert current year data to Recharts format
  const currentYearChartData = useMemo(() => {
    if (!processedCurrentYearData) return [];
    // Data is already in the correct format (cumulative or incremental) from parent
    // Just convert to Recharts format
    return convertToRechartsData(processedCurrentYearData);
  }, [processedCurrentYearData]);

  // Convert previous year data to Recharts format and align by daysFromStart
  const previousYearChartData = useMemo(() => {
    if (!processedPreviousYearData) {
      return [];
    }
    const chartData = convertToRechartsData(processedPreviousYearData);
    return chartData;
  }, [processedPreviousYearData]);

  // Merge chart data: combine current and previous year data by daysFromStart
  // Previous year data will be added with a prefix to distinguish it
  // Always use current year's dateStr for x-axis to avoid duplicate labels
  const chartData = useMemo(() => {
    const dayMap = new Map<number, RechartsDataPoint>();

    // Add current year data first - this establishes the date strings for x-axis
    currentYearChartData.forEach((point) => {
      const day = point.day;
      dayMap.set(day, { ...point });
    });

    let mergedCount = 0;
    // Add previous year data with prefix, aligned by daysFromStart
    // Only merge into existing entries (from current year) to ensure consistent x-axis labels
    previousYearChartData.forEach((point) => {
      const day = point.day;
      const existing = dayMap.get(day);

      // Only add previous year data if there's a corresponding current year entry
      // This ensures x-axis labels come from current year only
      if (existing) {
        mergedCount++;
        // Add previous year values with prefix
        Object.keys(point).forEach((key) => {
          if (key !== 'day' && key !== 'dateStr' && key !== 'fullDateStr') {
            existing[`prev_${key}`] = point[key];
          }
        });
        dayMap.set(day, existing);
      }
      // If no current year entry exists for this day, skip it to avoid duplicate x-axis labels
    });

    // Convert map to array and sort by day
    return Array.from(dayMap.values()).sort((a, b) => a.day - b.day);
  }, [currentYearChartData, previousYearChartData]);

  // Get current position from transformed data (already cumulative or incremental based on parent)
  // Select estimate based on current month/year:
  // - If current month/year exists in data, use that
  // - If data is from a past year, use the last estimate with breakdowns
  const currentActualPosition = useMemo(() => {
    if (!currentYearData?.estimates || currentYearData.estimates.length === 0) {
      return null;
    }

    const now = currentDate || new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth(); // 0-indexed (0 = January, 11 = December)

    // Sort estimates by date
    const sortedEstimates = [...currentYearData.estimates].sort(
      (a, b) => a.date.getTime() - b.date.getTime(),
    );

    // Filter to estimates with breakdowns (prefer those with data)
    const estimatesWithBreakdowns = sortedEstimates.filter(
      (est) => est.breakdowns && est.breakdowns.length > 0,
    );

    // Try to find an estimate that matches the current month/year
    const matchingEstimate = estimatesWithBreakdowns.find((estimate) => {
      const estimateYear = estimate.date.getFullYear();
      const estimateMonth = estimate.date.getMonth();
      return estimateYear === currentYear && estimateMonth === currentMonth;
    });

    // If we found a match for current month/year, use it
    if (matchingEstimate) {
      return matchingEstimate;
    }

    // Otherwise, use the last estimate with breakdowns (most recent data point)
    if (estimatesWithBreakdowns.length > 0) {
      return estimatesWithBreakdowns[estimatesWithBreakdowns.length - 1];
    }

    // Fallback to last estimate even if no breakdowns (shouldn't happen but be safe)
    return sortedEstimates[sortedEstimates.length - 1];
  }, [currentYearData, currentDate]);

  const currentPredictedPosition = useMemo(() => {
    if (
      !currentYearData?.predictions['linear-regression'] ||
      currentYearData.predictions['linear-regression'].length === 0
    ) {
      return null;
    }

    const now = currentDate || new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth(); // 0-indexed (0 = January, 11 = December)

    // Sort estimates by date
    const sortedPredictions = [
      ...currentYearData.predictions['linear-regression'],
    ].sort((a, b) => a.date.getTime() - b.date.getTime());

    // Try to find an estimate that matches the current month/year
    const matchingEstimate = sortedPredictions.find((prediction) => {
      const predictionYear = prediction.date.getFullYear();
      const predictionMonth = prediction.date.getMonth();
      return predictionYear === currentYear && predictionMonth === currentMonth;
    });

    // If we found a match for current month/year, use it
    if (matchingEstimate) {
      return matchingEstimate;
    }

    // Otherwise, use the last estimate (most recent data point)
    return sortedPredictions[sortedPredictions.length - 1];
  }, [currentYearData, currentDate]);

  // Calculate Y-axis domain and ticks for better spacing
  // Only include values from visible lines to ensure proper scaling when lines are toggled
  const { yAxisDomain, yAxisTicks } = useMemo(() => {
    if (chartData.length === 0) {
      return {
        yAxisDomain: [0, (dataMax: number) => dataMax * 1.02] as [
          number,
          (dataMax: number) => number,
        ],
        yAxisTicks: [] as number[],
      };
    }

    // Determine which data keys correspond to visible lines
    const visibleDataKeys = new Set<string>();

    // Map visible line types to their data keys in chartData
    const isEstimatesVisible = effectiveVisibleLines.has('estimates');
    if (isEstimatesVisible) {
      visibleDataKeys.add('estimates');
    }

    // Add prediction algorithm types if they are effectively visible
    // Predictions are gated behind estimates - they can only be visible when estimates is ON
    // Check for individual algorithm names (linear-regression, ai-forecasting, etc.)
    if (processedCurrentYearData?.types && isEstimatesVisible) {
      processedCurrentYearData.types
        .filter(
          (type) =>
            type !== 'estimates' &&
            type !== 'reported' &&
            type !== 'reported_future',
        )
        .forEach((type) => {
          // Check if this specific algorithm is visible
          // For linear-regression-past, check if linear-regression is visible
          if (type === 'linear-regression-past') {
            if (effectiveVisibleLines.has('linear-regression')) {
              visibleDataKeys.add(type);
            }
          } else {
            // For other types, check if the algorithm name itself is visible
            if (effectiveVisibleLines.has(type)) {
              visibleDataKeys.add(type);
            }
          }
        });
    }

    if (effectiveVisibleLines.has('reported')) {
      visibleDataKeys.add('reported');
      visibleDataKeys.add('reported_future'); // Extended future reported points
    }

    if (effectiveVisibleLines.has('previousYear')) {
      // Add previous year data keys (prev_estimates, prev_linear-regression, etc.)
      if (processedPreviousYearData?.types) {
        processedPreviousYearData.types.forEach((type) => {
          visibleDataKeys.add(`prev_${type}`);
        });
      }
    }

    // Extract only numeric values from visible data keys (excluding metadata fields)
    const allValues = chartData.flatMap((dataPoint) =>
      Object.entries(dataPoint)
        .filter(
          ([key]) =>
            key !== 'day' &&
            key !== 'dateStr' &&
            key !== 'fullDateStr' &&
            visibleDataKeys.has(key),
        )
        .map(([, value]) => (typeof value === 'number' ? value : 0)),
    );

    // Safety check: if no values found, default to [0, 0]
    if (allValues.length === 0) {
      return {
        yAxisDomain: [0, 100] as [number, number],
        yAxisTicks: [0, 25, 50, 75, 100] as number[],
      };
    }

    const minValue = Math.min(...allValues);
    const maxValue = Math.max(...allValues);

    // If there are negative values, allow domain to go below 0
    // Otherwise, start at 0 for better visualization
    const domainMin = minValue < 0 ? minValue * 1.02 : 0;
    const domainMax = maxValue * 1.02;

    const yAxisDomain: [number, number] = [domainMin, domainMax];

    // Calculate ticks based on domain range to ensure top tick aligns with domain max
    // This prevents double lines (tick line + domain boundary line)
    let yAxisTicks = chartConfig.getYAxisTicks(domainMin, domainMax);

    // Ensure $0 is always included in the ticks (if domain includes 0)
    if (domainMin <= 0 && domainMax >= 0 && !yAxisTicks.includes(0)) {
      yAxisTicks = [...yAxisTicks, 0].sort((a, b) => a - b);
    }

    return { yAxisDomain, yAxisTicks };
  }, [
    chartData,
    effectiveVisibleLines,
    processedCurrentYearData?.types,
    processedPreviousYearData?.types,
  ]);
  // Calculate totals from transformed data (already cumulative or incremental based on parent)
  const totals = useMemo(() => {
    if (!processedCurrentYearData) {
      return { totalOwed: 0, totalActual: 0 };
    }

    // Use totals from processed data (already in correct format from parent)
    const totalActual = processedCurrentYearData.totals.actualToDate;
    const totalOwed = processedCurrentYearData.totals.projectedTotal;

    return { totalOwed, totalActual };
  }, [processedCurrentYearData]);

  // Check if current year has any data (estimates, predictions, or reported)
  const hasCurrentYearData = useMemo(() => {
    if (!currentYearData) return false;

    const hasEstimates =
      currentYearData.estimates && currentYearData.estimates.length > 0;
    const hasPredictions =
      currentYearData.predictions &&
      Object.values(currentYearData.predictions).some(
        (points) => points && points.length > 0,
      );
    const hasReported =
      currentYearData.reported && currentYearData.reported.length > 0;

    return hasEstimates || hasPredictions || hasReported;
  }, [currentYearData]);

  // Calculate conditional vs non-conditional rebate breakdown
  const rebateBreakdown = useMemo(() => {
    if (!currentActualPosition) {
      return {
        conditionalRebateYtd: undefined,
        nonConditionalRebateYtd: undefined,
      };
    }
    return calculateConditionalVsNonConditionalRebates(
      currentActualPosition,
      rebateRules || [],
      isCumulative,
    );
  }, [currentActualPosition, rebateRules, isCumulative]);

  // Calculate tag totals from forecast breakdowns
  const tagTotals = useMemo(() => {
    if (!ruleTagsMap || !currentActualPosition) {
      return new Map<string, number>();
    }

    const totals = new Map<string, number>();

    const breakdownsToProcess = currentActualPosition.breakdowns || [];

    // Iterate through breakdowns and aggregate by tag
    breakdownsToProcess.forEach((breakdown) => {
      const ruleTags = ruleTagsMap.get(breakdown.ruleId);
      if (!ruleTags || ruleTags.length === 0) {
        // Skip rules with no tags (user confirmed: exclude from tag-based cards)
        return;
      }

      // Select the appropriate amount based on isCumulative
      const amount = isCumulative
        ? (breakdown.cumulativeRebateAmount ?? breakdown.rebateAmount)
        : breakdown.rebateAmount;

      // Add this breakdown's amount to all tags associated with the rule
      ruleTags.forEach((tagName) => {
        const currentTotal = totals.get(tagName) || 0;
        totals.set(tagName, currentTotal + amount);
      });
    });

    // Filter out tags with zero totals
    const nonZeroTotals = new Map<string, number>();
    totals.forEach((amount, tagName) => {
      if (Math.abs(amount) >= 0.01) {
        nonZeroTotals.set(tagName, amount);
      }
    });

    return nonZeroTotals;
  }, [ruleTagsMap, currentActualPosition, isCumulative]);

  // Custom tooltip component
  const CustomTooltip = ({
    active,
    payload,
    label,
  }: TooltipProps<number, string>) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload as RechartsDataPoint;
      // Filter out null/undefined values
      const validPayload = payload.filter(
        (entry) => entry.value !== null && entry.value !== undefined,
      );

      // Check if we're at a connection point (where past and future lines meet)
      // At connection points, we have both past and future versions
      const hasLinearRegressionPast =
        data['linear-regression-past'] !== null &&
        data['linear-regression-past'] !== undefined;
      const hasLinearRegressionFuture =
        data['linear-regression'] !== null &&
        data['linear-regression'] !== undefined;
      const hasReportedPast =
        data['reported'] !== null && data['reported'] !== undefined;
      const hasReportedFuture =
        data['reported_future'] !== null &&
        data['reported_future'] !== undefined;

      const isConnectionPoint =
        (hasLinearRegressionPast && hasLinearRegressionFuture) ||
        (hasReportedPast && hasReportedFuture);

      // At connection points, filter out blue lines (future versions)
      // Everywhere else, show all lines normally
      const filteredPayload = isConnectionPoint
        ? validPayload.filter(
            (entry) =>
              entry.dataKey !== 'linear-regression' &&
              entry.dataKey !== 'reported_future',
          )
        : validPayload;

      return (
        <div className="bg-white/95 text-gray-800 p-3 rounded-lg border border-gray-200 shadow-lg">
          <p className="mb-2 font-bold text-gray-800">
            Day {Number(label) + 1} • {data.fullDateStr}
          </p>

          {filteredPayload.map((entry, index: number) => (
            <p key={index} className="my-1 text-gray-800 font-medium">
              <span
                className="inline-block w-3 h-3 rounded-full mr-2"
                style={{ backgroundColor: entry.color }}
              ></span>
              {entry.name}: {formatCurrency(entry.value as number)}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  // Custom Legend with chevrons for split lines and clickable toggle buttons
  const CustomLegend = ({ payload }: LegendProps) => {
    // Build legend entries based on what SHOULD be shown, not just what's in payload
    // This ensures hidden lines still appear in the legend so they can be re-enabled
    const legendEntries: Array<{
      label: string;
      colors: string[];
      isSplit: boolean;
      lineType: string; // Key in visibleLines for visibility toggle
    }> = [];

    // Helper to get color from payload if available, otherwise use default
    const getColorFromPayload = (
      dataKey: string,
      defaultColor: string,
    ): string => {
      if (!payload) return defaultColor;
      const entry = payload.find((e) => e.dataKey === dataKey);
      return (entry?.color as string) || defaultColor;
    };

    // Check if we have prediction data (to determine if we should show split)
    const hasLinearRegressionPast = payload?.some(
      (entry) => entry.dataKey === 'linear-regression-past',
    );
    const hasLinearRegressionFuture = payload?.some(
      (entry) => entry.dataKey === 'linear-regression',
    );
    const hasReportedPast = payload?.some(
      (entry) => entry.dataKey === 'reported',
    );
    const hasReportedFuture = payload?.some(
      (entry) => entry.dataKey === 'reported_future',
    );

    // Check if we have data for each line type
    const hasEstimates =
      processedCurrentYearData?.types?.includes('estimates') ?? false;
    const hasPredictions =
      processedCurrentYearData?.types?.some(
        (type) =>
          type !== 'estimates' &&
          type !== 'reported' &&
          type !== 'reported_future',
      ) ?? false;
    const hasReported =
      processedCurrentYearData?.types?.includes('reported') ?? false;
    const hasPreviousYear = processedPreviousYearData !== null;

    // Always show Estimates if data exists
    if (hasEstimates) {
      legendEntries.push({
        label: 'Estimates',
        colors: [getColorFromPayload('estimates', colors.success.main)],
        isSplit: false,
        lineType: 'estimates',
      });
    }

    // Show individual prediction algorithm entries
    // First check which algorithms are available in the data
    const availableAlgorithmTypes =
      processedCurrentYearData?.types?.filter(
        (type) =>
          type !== 'estimates' &&
          type !== 'reported' &&
          type !== 'reported_future' &&
          type !== 'linear-regression-past', // This is handled with linear-regression
      ) ?? [];

    // Add entry for Linear Regression (with split support)
    if (
      availableAlgorithmTypes.includes('linear-regression') ||
      hasLinearRegressionPast
    ) {
      const hasSplit = hasLinearRegressionPast && hasLinearRegressionFuture;
      if (hasSplit) {
        legendEntries.push({
          label: PREDICTION_ALGORITHMS['linear-regression'].label,
          colors: [
            '#9C27B0', // Magenta for past
            PREDICTION_ALGORITHMS['linear-regression'].color, // Blue for future
          ],
          isSplit: true,
          lineType: 'linear-regression',
        });
      } else {
        legendEntries.push({
          label: PREDICTION_ALGORITHMS['linear-regression'].label,
          colors: [PREDICTION_ALGORITHMS['linear-regression'].color],
          isSplit: false,
          lineType: 'linear-regression',
        });
      }
    }

    // Add entries for other prediction algorithms
    (
      ['ai-forecasting', 'sarima', 'yoy-seasonal'] as PredictionAlgorithm[]
    ).forEach((algorithm) => {
      if (availableAlgorithmTypes.includes(algorithm)) {
        const algorithmInfo = PREDICTION_ALGORITHMS[algorithm];
        legendEntries.push({
          label: algorithmInfo.label,
          colors: [algorithmInfo.color],
          isSplit: false,
          lineType: algorithm,
        });
      }
    });

    // Always show Reported if data exists
    if (hasReported) {
      const hasSplit = hasReportedPast && hasReportedFuture;
      if (hasSplit) {
        legendEntries.push({
          label: 'Reported',
          colors: [
            getColorFromPayload('reported', colors.black.main), // Black
            getColorFromPayload('reported_future', colors.info.main), // Blue
          ],
          isSplit: true,
          lineType: 'reported',
        });
      } else {
        legendEntries.push({
          label: 'Reported',
          colors: [getColorFromPayload('reported', colors.black.main)],
          isSplit: false,
          lineType: 'reported',
        });
      }
    }

    // Always show Previous Year if data exists
    if (hasPreviousYear) {
      legendEntries.push({
        label: 'Previous Year',
        colors: [getColorFromPayload('prev_estimates', colors.grey[600])],
        isSplit: false,
        lineType: 'previousYear',
      });
    }

    // Check if estimates is visible (predictions are gated behind estimates)
    const isEstimatesVisible = effectiveVisibleLines.has('estimates');

    // Check if a line type is a prediction algorithm
    const isPredictionAlgorithm = (lineType: string): boolean => {
      return ALL_PREDICTION_ALGORITHMS.includes(
        lineType as PredictionAlgorithm,
      );
    };

    // Handle visibility toggle
    const handleToggleVisibility = (lineType: string) => {
      if (!onVisibleLinesChange) return;

      // Don't allow toggling predictions when estimates is OFF
      if (isPredictionAlgorithm(lineType) && !isEstimatesVisible) {
        return;
      }

      const isVisible = effectiveVisibleLines.has(lineType);
      const newVisible = new Set(effectiveVisibleLines);

      // Prevent unchecking if it's the last visible line
      if (isVisible && effectiveVisibleLines.size === 1) {
        return; // Don't allow unchecking last line
      }

      if (isVisible) {
        newVisible.delete(lineType);
      } else {
        newVisible.add(lineType);
      }
      onVisibleLinesChange(newVisible);
    };

    // Don't render if no entries (shouldn't happen, but safety check)
    if (legendEntries.length === 0) {
      return null;
    }

    // Separate entries into groups for hierarchical display
    const estimatesEntry = legendEntries.find(
      (e) => e.lineType === 'estimates',
    );
    const predictionEntries = legendEntries.filter((e) =>
      isPredictionAlgorithm(e.lineType),
    );
    const otherEntries = legendEntries.filter(
      (e) => e.lineType !== 'estimates' && !isPredictionAlgorithm(e.lineType),
    );

    // Helper to render a single legend button
    const renderLegendButton = (
      entry: (typeof legendEntries)[0],
      index: number,
      isNested = false,
    ) => {
      const isVisible = effectiveVisibleLines.has(entry.lineType);
      const isGatedPrediction =
        isPredictionAlgorithm(entry.lineType) && !isEstimatesVisible;
      const isEffectivelyVisible = isVisible && !isGatedPrediction;

      const isDisabled =
        !onVisibleLinesChange ||
        isGatedPrediction ||
        (isVisible && effectiveVisibleLines.size === 1);

      return (
        <button
          key={`${entry.lineType}-${index}`}
          type="button"
          onClick={() => handleToggleVisibility(entry.lineType)}
          disabled={isDisabled}
          title={isGatedPrediction ? 'Requires Estimates to be visible' : ''}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: isNested ? '4px 10px' : '6px 12px',
            border: '1px solid #E5E7EB',
            borderRadius: '6px',
            backgroundColor: isEffectivelyVisible ? '#FFFFFF' : '#F9FAFB',
            cursor: isDisabled ? 'not-allowed' : 'pointer',
            opacity: isEffectivelyVisible ? 1 : 0.5,
            transition: 'all 0.2s ease',
            fontSize: isNested ? '13px' : '14px',
          }}
          onMouseEnter={(e) => {
            if (!isDisabled) {
              e.currentTarget.style.backgroundColor = isEffectivelyVisible
                ? '#F3F4F6'
                : '#F9FAFB';
              e.currentTarget.style.borderColor = '#D1D5DB';
            }
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = isEffectivelyVisible
              ? '#FFFFFF'
              : '#F9FAFB';
            e.currentTarget.style.borderColor = '#E5E7EB';
          }}
        >
          {entry.isSplit ? (
            <div
              className="w-3 h-3 rounded-full flex-shrink-0"
              style={{
                background: isEffectivelyVisible
                  ? `linear-gradient(to right, ${entry.colors[0]} 50%, ${entry.colors[1]} 50%)`
                  : 'linear-gradient(to right, #9CA3AF 50%, #9CA3AF 50%)',
              }}
            />
          ) : (
            <div
              className={isNested ? 'w-2.5 h-2.5' : 'w-3 h-3'}
              style={{
                borderRadius: '50%',
                flexShrink: 0,
                backgroundColor: isEffectivelyVisible
                  ? entry.colors[0]
                  : '#9CA3AF',
              }}
            />
          )}
          <span
            style={{
              color: isEffectivelyVisible ? '#374151' : '#9CA3AF',
            }}
          >
            {entry.label}
          </span>
        </button>
      );
    };

    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          flexWrap: 'wrap',
          gap: '24px',
          paddingTop: '20px',
          alignItems: 'flex-start',
        }}
      >
        {/* Estimates + Predictions Group */}
        {estimatesEntry && (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '8px',
              padding: '8px',
              borderRadius: '8px',
              backgroundColor: isEstimatesVisible ? '#F0FDF4' : '#F9FAFB',
              border: `1px solid ${isEstimatesVisible ? '#BBF7D0' : '#E5E7EB'}`,
            }}
          >
            {/* Estimates toggle */}
            {renderLegendButton(estimatesEntry, 0)}

            {/* Prediction algorithms - nested under estimates */}
            {predictionEntries.length > 0 && (
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '6px',
                  paddingLeft: '12px',
                  borderLeft: `2px solid ${isEstimatesVisible ? '#86EFAC' : '#D1D5DB'}`,
                  marginLeft: '6px',
                }}
              >
                {predictionEntries.map((entry, index) =>
                  renderLegendButton(entry, index, true),
                )}
              </div>
            )}
          </div>
        )}

        {/* Other entries (Reported, Previous Year) - not grouped */}
        {otherEntries.map((entry, index) => renderLegendButton(entry, index))}
      </div>
    );
  };

  // Use shared formatting utilities
  const formatYAxis = chartFormatters.formatYAxis;

  // Generate month-end ticks based on forecast period (using UTC to avoid timezone issues)
  const xAxisTicks = useMemo(() => {
    const period = currentYearData?.forecastPeriod;
    if (!period?.startDate) {
      // Default: approximate month-end days for a calendar year starting Jan 1
      // Include day 0 for Jan 1
      return [0, 30, 58, 89, 119, 150, 180, 211, 242, 272, 303, 333, 364];
    }

    const startDate = new Date(period.startDate);
    const endDate = new Date(period.endDate);
    const ticks: number[] = [0]; // Start with day 0 (period start date)

    // Use UTC to avoid timezone issues - backend sends UTC dates
    let currentYear = startDate.getUTCFullYear();
    let currentMonth = startDate.getUTCMonth();
    const endYear = endDate.getUTCFullYear();
    const endMonth = endDate.getUTCMonth();

    while (
      currentYear < endYear ||
      (currentYear === endYear && currentMonth <= endMonth)
    ) {
      // Get last day of current month in UTC (day 0 of next month = last day of current month)
      const lastDayOfMonth = new Date(
        Date.UTC(currentYear, currentMonth + 1, 0),
      );

      // Calculate days from start using UTC timestamps
      const daysFromStart = Math.round(
        (lastDayOfMonth.getTime() - startDate.getTime()) /
          (1000 * 60 * 60 * 24),
      );

      if (daysFromStart >= 0 && lastDayOfMonth <= endDate) {
        ticks.push(daysFromStart);
      }

      // Move to next month
      currentMonth++;
      if (currentMonth > 11) {
        currentMonth = 0;
        currentYear++;
      }
    }

    // Add the period end date as the last tick to ensure domain alignment
    const endDaysFromStart = Math.round(
      (endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24),
    );
    if (endDaysFromStart >= 0 && !ticks.includes(endDaysFromStart)) {
      ticks.push(endDaysFromStart);
    }

    return ticks.sort((a, b) => a - b);
  }, [currentYearData?.forecastPeriod]);

  // Calculate X-axis domain based on actual data range to prevent double lines
  // Domain max should exactly match the last tick to avoid extra space at the right edge
  const xAxisDomain = useMemo(() => {
    if (chartData.length === 0) {
      return [0, 365] as [number, number];
    }
    const days = chartData.map((point) => point.day);
    const minDay = Math.min(...days);
    // Use the last tick value as the domain max to ensure perfect alignment
    // This prevents extra space beyond the last tick
    const lastTick =
      xAxisTicks.length > 0
        ? xAxisTicks[xAxisTicks.length - 1]
        : Math.max(...days);
    return [minDay, lastTick] as [number, number];
  }, [chartData, xAxisTicks]);

  // Format X-axis: calculate month label from day number (using UTC)
  const formatXAxis = (value: number) => {
    // First try to find in chart data
    const dataPoint = chartData.find((d) => d.day === value);
    if (dataPoint?.dateStr) {
      return dataPoint.dateStr;
    }

    // Calculate the date from day number and forecast period start (using UTC)
    const period = currentYearData?.forecastPeriod;
    if (period?.startDate) {
      const startDate = new Date(period.startDate);
      // Add days in milliseconds to avoid timezone issues
      const targetDate = new Date(
        startDate.getTime() + value * 24 * 60 * 60 * 1000,
      );

      const monthNames = [
        'Jan',
        'Feb',
        'Mar',
        'Apr',
        'May',
        'Jun',
        'Jul',
        'Aug',
        'Sep',
        'Oct',
        'Nov',
        'Dec',
      ];
      return `${monthNames[targetDate.getUTCMonth()]} ${targetDate.getUTCDate()}`;
    }

    return value.toString();
  };

  // Common props for reference lines
  const referenceLineProps = {
    stroke: '#666',
    strokeWidth: 1,
    ifOverflow: 'extendDomain' as const,
  };

  return (
    <Card className="mb-6">
      <CardContent className="p-6">
        {/* Title */}
        {title && (
          <div className="mb-6">
            <h2 className="text-xl font-medium text-foreground">{title}</h2>
          </div>
        )}

        {/* Main Forecast Chart */}
        <div className={showStatusCards ? 'mb-6' : ''}>
          {isLoadingForecast ? (
            <div className="w-full" style={{ height: chartConfig.chartHeight }}>
              <LoadingSpinner
                text="Loading forecast data..."
                centered
                minHeight={chartConfig.chartHeight}
              />
            </div>
          ) : !currentYearData || !hasCurrentYearData ? (
            <div
              className="w-full flex flex-col items-center justify-center text-muted-foreground"
              style={{ height: chartConfig.chartHeight }}
            >
              <svg
                className="w-16 h-16 mb-4 text-muted-foreground/50"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                />
              </svg>
              <p className="text-lg font-medium">No forecast data available</p>
              <p className="text-sm mt-1">
                There are no estimates, predictions, or reported values for this
                period.
              </p>
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={chartConfig.chartHeight}>
              <LineChart data={chartData} margin={chartConfig.chartMargin}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="day"
                  type="number"
                  tickFormatter={formatXAxis}
                  angle={-45}
                  textAnchor="end"
                  height={80}
                  ticks={xAxisTicks}
                  domain={xAxisDomain}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  tickFormatter={formatYAxis}
                  domain={yAxisDomain}
                  ticks={yAxisTicks}
                  axisLine={false}
                />
                <ReferenceLine y={0} {...referenceLineProps} />
                <ReferenceLine x={0} {...referenceLineProps} />
                <Tooltip content={<CustomTooltip />} />
                <Legend content={<CustomLegend />} />

                {/* Previous Year Actual Values Line (if available and visible) */}
                {effectiveVisibleLines.has('previousYear') &&
                  processedPreviousYearData?.types.includes('estimates') && (
                    <Line
                      type="monotone"
                      dataKey="prev_estimates"
                      stroke={colors.grey?.[400] || '#9CA3AF'} // Gray/muted color for previous year
                      strokeWidth={3}
                      dot={{
                        fill: colors.grey?.[400] || '#9CA3AF',
                        strokeWidth: 2,
                        r: 4,
                      }}
                      name={`Previous Year (${
                        multiYearForecastData?.currentYear
                          ? multiYearForecastData.currentYear - 1
                          : 'Previous'
                      })`}
                      activeDot={{
                        r: 6,
                        stroke: colors.grey?.[400] || '#9CA3AF',
                        strokeWidth: 2,
                      }}
                      connectNulls={false}
                      strokeDasharray="5 5" // Dashed line for previous year
                    />
                  )}
                {/* Previous Year: If no estimates, show predictions as main line */}
                {effectiveVisibleLines.has('previousYear') &&
                  !processedPreviousYearData?.types.includes('estimates') &&
                  processedPreviousYearData?.types.some((type) =>
                    type.includes('linear-regression'),
                  ) && (
                    <Line
                      type="monotone"
                      dataKey="prev_linear-regression"
                      stroke={colors.grey?.[400] || '#9CA3AF'} // Gray/muted color for previous year
                      strokeWidth={3}
                      dot={{
                        fill: colors.grey?.[400] || '#9CA3AF',
                        strokeWidth: 2,
                        r: 4,
                      }}
                      name={`Previous Year (${
                        multiYearForecastData?.currentYear
                          ? multiYearForecastData.currentYear - 1
                          : 'Previous'
                      })`}
                      activeDot={{
                        r: 6,
                        stroke: colors.grey?.[400] || '#9CA3AF',
                        strokeWidth: 2,
                      }}
                      connectNulls={false}
                      strokeDasharray="5 5" // Dashed line for previous year
                    />
                  )}

                {/* Previous Year Prediction Lines (if available and visible) */}
                {effectiveVisibleLines.has('previousYear') &&
                  processedPreviousYearData?.types
                    .filter((type) => type !== 'estimates')
                    .map((seriesKey) => {
                      const prevDataKey = `prev_${seriesKey}`;
                      const lineColor = colors.grey?.[500] || '#6B7280'; // Lighter gray for previous year predictions
                      const dash = '5 5';
                      const legendLabel = `Previous Year (${seriesKey})`;

                      return (
                        <Line
                          key={prevDataKey}
                          type="monotone"
                          dataKey={prevDataKey}
                          stroke={lineColor}
                          strokeWidth={2}
                          strokeDasharray={dash}
                          dot={false}
                          name={legendLabel}
                          activeDot={{
                            r: 4,
                            stroke: lineColor,
                            strokeWidth: 1,
                          }}
                          connectNulls={false}
                        />
                      );
                    })}

                {/* Future Prediction Lines - Linear Regression (Blue by default) */}
                {/* Predictions are gated behind estimates */}
                {isPredictionEffectivelyVisible('linear-regression') &&
                  processedCurrentYearData?.types.includes(
                    'linear-regression',
                  ) && (
                    <Line
                      type="monotone"
                      dataKey="linear-regression"
                      stroke={PREDICTION_ALGORITHMS['linear-regression'].color}
                      strokeWidth={4}
                      dot={false}
                      name={PREDICTION_ALGORITHMS['linear-regression'].label}
                      activeDot={{
                        r: 6,
                        stroke:
                          PREDICTION_ALGORITHMS['linear-regression'].color,
                        strokeWidth: 2,
                      }}
                      connectNulls={false}
                    />
                  )}
                {/* Handle other prediction algorithm types */}
                {/* Predictions are gated behind estimates */}
                {processedCurrentYearData?.types
                  .filter(
                    (type) =>
                      type !== 'linear-regression' &&
                      type !== 'linear-regression-past' &&
                      type !== 'estimates' &&
                      type !== 'reported' &&
                      type !== 'reported_future' &&
                      // Only render if this algorithm is effectively visible (has estimates)
                      isPredictionEffectivelyVisible(type),
                  )
                  .map((seriesKey) => {
                    // Get color from PREDICTION_ALGORITHMS or default to blue
                    const algorithmInfo =
                      PREDICTION_ALGORITHMS[seriesKey as PredictionAlgorithm];
                    const lineColor = algorithmInfo?.color || colors.info.main;
                    const lineName = algorithmInfo?.label || seriesKey;

                    return (
                      <Line
                        key={seriesKey}
                        type="monotone"
                        dataKey={seriesKey}
                        stroke={lineColor}
                        strokeWidth={4}
                        dot={false}
                        name={lineName}
                        activeDot={{
                          r: 6,
                          stroke: lineColor,
                          strokeWidth: 2,
                        }}
                        connectNulls={false}
                      />
                    );
                  })}

                {/* Past Prediction Lines (Magenta) - predictions past */}
                {/* Predictions are gated behind estimates */}
                {isPredictionEffectivelyVisible('linear-regression') &&
                  processedCurrentYearData?.types.includes(
                    'linear-regression-past',
                  ) && (
                    <Line
                      type="monotone"
                      dataKey="linear-regression-past"
                      stroke="#9C27B0"
                      strokeWidth={4}
                      dot={{ fill: '#9C27B0', strokeWidth: 2, r: 6 }}
                      name="Linear Regression (Past)"
                      activeDot={{
                        r: 8,
                        stroke: '#9C27B0',
                        strokeWidth: 2,
                      }}
                      connectNulls={false}
                    />
                  )}

                {/* Reported Future (Blue) Line - extended future predictions */}
                {effectiveVisibleLines.has('reported') &&
                  processedCurrentYearData?.types.includes(
                    'reported_future',
                  ) && (
                    <Line
                      type="monotone"
                      dataKey="reported_future"
                      stroke={colors.info.main}
                      strokeWidth={4}
                      dot={{ fill: colors.info.main, strokeWidth: 2, r: 6 }}
                      name="Reported (Future)"
                      activeDot={{
                        r: 8,
                        stroke: colors.info.main,
                        strokeWidth: 2,
                      }}
                      connectNulls={false}
                    />
                  )}

                {/* Reported (Black) Line - real reported values (reported past) */}
                {effectiveVisibleLines.has('reported') &&
                  processedCurrentYearData?.types.includes('reported') && (
                    <Line
                      type="monotone"
                      dataKey="reported"
                      stroke={colors.black.main}
                      strokeWidth={4}
                      dot={{ fill: colors.black.main, strokeWidth: 2, r: 6 }}
                      name="Reported (Black)"
                      activeDot={{
                        r: 8,
                        stroke: colors.black.main,
                        strokeWidth: 2,
                      }}
                      connectNulls={false}
                    />
                  )}

                {/* Current Year Actual Values Line - Estimates (render last, top layer) */}
                {effectiveVisibleLines.has('estimates') &&
                  processedCurrentYearData?.types.includes('estimates') && (
                    <Line
                      type="monotone"
                      dataKey="estimates"
                      stroke={colors.success.main}
                      strokeWidth={4}
                      dot={{ fill: colors.success.main, strokeWidth: 2, r: 6 }}
                      name="Actual"
                      activeDot={{
                        r: 8,
                        stroke: colors.success.main,
                        strokeWidth: 2,
                      }}
                      connectNulls={false}
                    />
                  )}
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Status Cards - only show when we have data */}
        {showStatusCards && hasCurrentYearData && (
          <RebateStatusCards
            totalOwed={totals.totalActual}
            totalProjected={totals.totalOwed}
            currentActualPosition={currentActualPosition || undefined}
            currentPredictedPosition={currentPredictedPosition || undefined}
            forecastPeriod={currentYearData?.forecastPeriod}
            compact={compact}
            summaryStyle={true}
            rebateRules={rebateRules}
            conditionalRebateYtd={rebateBreakdown.conditionalRebateYtd}
            nonConditionalRebateYtd={rebateBreakdown.nonConditionalRebateYtd}
            isCumulative={isCumulative}
            tagTotals={tagTotals}
            tagNameToIdMap={tagNameToIdMap}
            overallMetrics={
              currentYearData?.overallMetrics
                ? currentYearData.overallMetrics
                : currentYearData
                  ? {
                      // Fallback: calculate from breakdown records if backend doesn't provide overallMetrics
                      // Note: This will have n-counted volumes, so backend should always provide overallMetrics
                      actual: calculateOverallRebateMetrics(
                        currentYearData.estimates || [],
                        true, // Always cumulative for YTD percentage
                      ),
                      predicted: calculateOverallRebateMetrics(
                        currentYearData.predictions?.['linear-regression'] ||
                          [],
                        true, // Always cumulative for term-end percentage
                      ),
                    }
                  : undefined
            }
          />
        )}
      </CardContent>
    </Card>
  );
};

export default ForecastCard;
